import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useRecoilState } from 'recoil'
import styled from 'styled-components'

import { usePasswordChange } from '~/features/auth/queries/useAuth'
import Check from '~/shared/assets/icons/Check'
import Close from '~/shared/assets/icons/Close'
import Button from '~/shared/components/buttons/Button'
import { checkEmailValidation } from '~/shared/components/modals/auth/checkEmailValidation'
import { isModalOpenState, keyCodeState } from '~/shared/states'
import theme from '~/shared/styles/theme'

function PasswordChangeModal() {
  const { enqueueSnackbar } = useSnackbar()
  const [_, setIsModalOpen] = useRecoilState(isModalOpenState)
  const [passwordChangeKeyCode, setPasswordChangeKeyCode] =
    useRecoilState(keyCodeState)

  const [email, setEmail] = useState<string>('')

  const [isEmailValidated, setIsEmailValidated] = useState<boolean>(true)
  const [isEmailSent] = useState<boolean>(false)

  const { mutate: passwordChange, isPending } = usePasswordChange()

  useEffect(() => {
    if (email) {
      setIsEmailValidated(checkEmailValidation(email))
    }
  }, [email])

  const emailInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const emailInputKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key || event.keyCode

    if (key === 'Enter' || key === 13) {
      requestPasswordChange()
    }
  }

  const requestPasswordChange = () => {
    if (!isEmailValidated) {
      enqueueSnackbar('이메일이 유효하지 않습니다.', { variant: 'error' })
    } else {
      enqueueSnackbar('인증 메일 전송을 요청합니다.', {
        variant: 'success',
      })
      passwordChange(email, {
        onSuccess: (res) => {
          if (res.errorCode === -2001) {
            enqueueSnackbar(res.errorMsg, { variant: 'error' })
          } else {
            enqueueSnackbar('인증 메일 전송이 완료되었습니다.', {
              variant: 'success',
            })
            setPasswordChangeKeyCode({
              ...passwordChangeKeyCode,
              key: res.data.key,
            })

            setIsModalOpen({
              contents: 'password-change-input-code',
              isOpen: true,
            })
          }
        },
      })
    }
  }

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <CloseIcon
        onClick={() => setIsModalOpen({ contents: '', isOpen: false })}
      />
      <Title>비밀번호 재설정</Title>
      <Desc>
        회원가입 시 등록하신 이메일 주소를 입력하면, <br /> 해당 이메일로
        비밀번호 변경 코드를 보내드립니다.
      </Desc>

      <InputBox>
        <Input
          placeholder="Email"
          onChange={emailInputChange}
          onKeyUp={emailInputKeyUp}
          className={`${!isEmailValidated && 'warning'}`}
        />
        <InputDesc
          className={`${isEmailSent && 'ok'} ${!isEmailValidated && 'warning'}`}
        >
          {isEmailSent && '정상적으로 이메일이 발송되었습니다.'}
          {isEmailSent && <Check color={theme.colors.mint_3} />}
          {!isEmailSent &&
            !isEmailValidated &&
            '*유효한 이메일 주소를 입력해주세요'}
        </InputDesc>
      </InputBox>
      {!isPending && (
        <SendButton
          onClick={requestPasswordChange}
          $width={530}
          $height={48}
          $fontSize={18}
          $variant="black"
        >
          보내기
        </SendButton>
      )}
    </Wrapper>
  )
}

export default PasswordChangeModal

const Wrapper = styled.section`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  box-sizing: border-box;
  width: 684px;
  height: auto;
  margin: 0 auto;
  padding: 108px 77px 60px;

  background-color: ${theme.colors.white};
  border-radius: 12px;

  @media ${theme.device.medium} {
    gap: 24px;
    width: 90%;
    height: auto;
    padding: 56px 20px 30px;
  }
`

const CloseIcon = styled(Close)`
  position: absolute;
  top: 36px;
  right: 36px;

  @media ${theme.device.medium} {
    top: 20px;
    right: 20px;
    width: 24px;
    height: 24px;
  }
`

const Title = styled.h1`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 500;

  @media ${theme.device.medium} {
    font-size: 36px;
  }
`

const Desc = styled.div`
  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  line-height: 25px;
  text-align: center;

  @media ${theme.device.medium} {
    font-size: 15px;
    line-height: 21px;
  }
`

const InputBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 49px;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Light, sans-serif;
  font-size: 18px;

  background-color: ${theme.colors.grey_1};
  border: none;
  border-radius: 12px;

  &.warning {
    border: 1px solid ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const InputDesc = styled.div`
  display: flex;
  align-items: center;

  margin: 12px 0;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;

  &.warning {
    color: ${theme.colors.warning};
  }

  &.ok {
    color: ${theme.colors.mint_3};
  }
`

const SendButton = styled(Button)`
  width: 100%;
  font-size: 18px;
  color: ${theme.colors.white};
  background-color: ${theme.colors.grey_5};

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`
